import { Icon, SegmentedCard, Button } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SendTestEmail } from '@hero/components/modals/SendTestEmail';
import { SubjectLine } from '@hero/components/organisms/GiftExperience';
import { SHARING_METHODS } from '@hero/constants';
import { useDebounceEffect } from '@hero/hooks';
import { ReviewSection } from '@hero/molecules';

import * as S from './style';

export function HowToSendSection({
  isCompleted,
  isContactless,
  isLoading,
  onSharingMethod,
  onSharePreview,
  sharingMethod,
  stepIndex = 3
}) {
  const { t } = useTranslation();

  const DEFAULT_SUBJECT = t('justSentYouAGift', { name: '{{senderName}}' });

  const [subject, setSubject] = useState(DEFAULT_SUBJECT);

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  useDebounceEffect(
    async () => {
      if (subject !== DEFAULT_SUBJECT) {
        onSharingMethod({ sharingMethod: SHARING_METHODS.email, subject });
      }
    },
    1000,
    [subject]
  );

  useEffect(() => {
    if (isContactless && sharingMethod !== SHARING_METHODS.none) {
      onSharingMethod({ sharingMethod: SHARING_METHODS.none });
    }
  }, [isContactless]);

  return (
    <ReviewSection
      stepIndex={stepIndex}
      isCompleted={isCompleted}
      title={t('howToSend')}
      subTitle={t('howToSendDescription')}
    >
      <S.HTSBody>
        <SegmentedCard
          activeTab={sharingMethod}
          onChange={sharingMethod => onSharingMethod({ sharingMethod, subject })}
          updateHash={false}
          disabled={isLoading}
          options={[
            {
              value: SHARING_METHODS.email,
              title: t('howToSendOption1Title'),
              description: (
                <S.TabDescription as="div" variant="body2">
                  {t('howToSendOption1Description')}
                </S.TabDescription>
              ),
              children: (
                <S.HTSOption>
                  <S.HTSOptionBody>
                    <SubjectLine subject={subject} setSubject={setSubject} />
                  </S.HTSOptionBody>

                  <Button
                    variant="link"
                    style={{ marginTop: 24 }}
                    onClick={() => setIsEmailModalOpen(true)}
                    startIcon={<Icon name="email" />}
                  >
                    {t('sendTestEmail')}
                  </Button>

                  <SendTestEmail
                    isOpen={isEmailModalOpen}
                    onRequestClose={() => setIsEmailModalOpen(false)}
                    onAsyncSubmit={onSharePreview}
                  />
                </S.HTSOption>
              ),
              disabled: !!isContactless,
              icon: <Icon name="email" size="xl" />
            },
            {
              value: SHARING_METHODS.none,
              title: t('howToSendOption2Title'),
              description: (
                <S.TabDescription as="div" variant="body2">
                  {t('howToSendOption2Description')}
                </S.TabDescription>
              ),
              disabled: false,
              icon: <Icon name="url" size="xl" />
            }
          ]}
        />
      </S.HTSBody>
    </ReviewSection>
  );
}

HowToSendSection.propTypes = {
  isCompleted: PropTypes.bool,
  isContactless: PropTypes.bool,
  isLoading: PropTypes.bool,
  onSharingMethod: PropTypes.func,
  onSharePreview: PropTypes.func,
  secureSingleLink: PropTypes.string,
  sharingMethod: PropTypes.string,
  stepIndex: PropTypes.number
};
